import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import auditTrailService from '../services/auditTrail';
import { useApps } from '../contexts/AppsContext';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';

const AuditTrailPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    const [actions, setActions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
    const [expandedEntry, setExpandedEntry] = useState(null);

    const [filters, setFilters] = useState({
        action: '',
        actor_user_id: '',
        resource_type: '',
        from_date: '',
        to_date: '',
    });

    useEffect(() => {
        if (apps?.length) {
            const id = resolveDefaultAppId(apps, searchParams.get('app_id'));
            setAppId(id);
            loadTrail(id, 1);
            loadActions(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadTrail = async (id, page) => {
        setLoading(true);
        setError('');
        try {
            const params = { page, limit: 50 };
            Object.entries(filters).forEach(([k, v]) => {
                if (v) params[k] = v;
            });
            const data = await auditTrailService.getTrail(id || appId, params);
            setEntries(data?.results || []);
            setPagination(data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
        } catch (err) {
            if (err?.response?.status === 400 && err?.response?.data?.message?.includes('not enabled')) {
                setError(
                    'Audit trail is not enabled for this application. Enable it in app_config.audit_trail_enabled.'
                );
            } else {
                setError(err?.response?.data?.message || 'Failed to load audit trail.');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadActions = async (id) => {
        try {
            const data = await auditTrailService.getActions(id || appId);
            setActions(data || []);
        } catch {
            setActions([]);
        }
    };

    const handleFilter = () => loadTrail(appId, 1);

    const handleClearFilters = () => {
        setFilters({ action: '', actor_user_id: '', resource_type: '', from_date: '', to_date: '' });
        loadTrail(appId, 1);
    };

    const handleExportCSV = () => {
        if (!entries.length) return;
        const headers = ['Timestamp', 'Actor', 'Action', 'Resource Type', 'Resource ID', 'IP Address'];
        const rows = entries.map((e) => [
            e.created_on,
            `${e.first_name || ''} ${e.last_name || ''} (${e.username || ''})`,
            e.action,
            e.resource_type || '',
            e.resource_id || '',
            e.ip_address || '',
        ]);
        const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loadingApps) return <SkeletonList />;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Audit Trail"
                subtitle="View structured audit logs"
                actions={
                    <div className="flex items-center gap-3">
                        <select
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                            value={appId}
                            onChange={(e) => {
                                setAppId(e.target.value);
                                setSelectedAppId(e.target.value);
                                loadTrail(e.target.value, 1);
                                loadActions(e.target.value);
                            }}
                        >
                            {apps?.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.app_name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleExportCSV}
                            disabled={!entries.length}
                            className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-40"
                        >
                            <Download size={14} /> Export CSV
                        </button>
                    </div>
                }
            />

            {/* Filters */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <select
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        value={filters.action}
                        onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                    >
                        <option value="">All actions</option>
                        {actions.map((a) => (
                            <option key={a} value={a}>
                                {a}
                            </option>
                        ))}
                    </select>
                    <input
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        placeholder="Actor user ID"
                        value={filters.actor_user_id}
                        onChange={(e) => setFilters({ ...filters, actor_user_id: e.target.value })}
                    />
                    <input
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        placeholder="Resource type"
                        value={filters.resource_type}
                        onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
                    />
                    <input
                        type="date"
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        value={filters.from_date}
                        onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                    />
                    <input
                        type="date"
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        value={filters.to_date}
                        onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                    />
                </div>
                <div className="flex gap-2 mt-3">
                    <button
                        onClick={handleFilter}
                        className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Apply Filters
                    </button>
                    <button
                        onClick={handleClearFilters}
                        className="px-4 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                    >
                        Clear
                    </button>
                    <span className="text-xs text-slate-400 self-center ml-auto">
                        {pagination.total} result{pagination.total !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {error && (
                <div className="space-y-2">
                    <ErrorState message={error} />
                    <button
                        onClick={() => {
                            setError('');
                            loadTrail(appId, 1);
                        }}
                        className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        Retry
                    </button>
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : entries.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center text-slate-500">
                    No audit entries found.
                </div>
            ) : (
                <>
                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left text-slate-600">
                                    <th className="px-4 py-2.5 font-medium">Timestamp</th>
                                    <th className="px-4 py-2.5 font-medium">Actor</th>
                                    <th className="px-4 py-2.5 font-medium">Action</th>
                                    <th className="px-4 py-2.5 font-medium">Resource</th>
                                    <th className="px-4 py-2.5 font-medium">IP</th>
                                    <th className="px-4 py-2.5 font-medium w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((e) => (
                                    <React.Fragment key={e.audit_id}>
                                        <tr
                                            className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                            onClick={() =>
                                                setExpandedEntry(expandedEntry === e.audit_id ? null : e.audit_id)
                                            }
                                        >
                                            <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                                                {new Date(e.created_on).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-2">
                                                {e.first_name || e.username || e.actor_user_id || 'system'}
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className="px-2 py-0.5 rounded bg-slate-100 text-xs font-medium">
                                                    {e.action}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-slate-500">
                                                {e.resource_type}
                                                {e.resource_id ? ` / ${e.resource_id.slice(0, 8)}...` : ''}
                                            </td>
                                            <td className="px-4 py-2 text-slate-400 text-xs">{e.ip_address || '-'}</td>
                                            <td className="px-4 py-2">
                                                {expandedEntry === e.audit_id ? (
                                                    <ChevronUp size={14} />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                )}
                                            </td>
                                        </tr>
                                        {expandedEntry === e.audit_id && (
                                            <tr className="bg-slate-50">
                                                <td colSpan={6} className="px-4 py-3">
                                                    <div className="text-xs space-y-1">
                                                        <p>
                                                            <span className="font-medium">Audit ID:</span> {e.audit_id}
                                                        </p>
                                                        <p>
                                                            <span className="font-medium">User Agent:</span>{' '}
                                                            {e.user_agent || '-'}
                                                        </p>
                                                        <p>
                                                            <span className="font-medium">Tenant:</span>{' '}
                                                            {e.tenant_id || 'N/A'}
                                                        </p>
                                                        {e.details_json && (
                                                            <div>
                                                                <span className="font-medium">Details:</span>
                                                                <pre className="mt-1 p-2 bg-white border rounded text-xs overflow-x-auto max-h-40">
                                                                    {JSON.stringify(
                                                                        typeof e.details_json === 'string'
                                                                            ? JSON.parse(e.details_json)
                                                                            : e.details_json,
                                                                        null,
                                                                        2
                                                                    )}
                                                                </pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => loadTrail(appId, pagination.page - 1)}
                                className="px-3 py-1 text-sm rounded border hover:bg-slate-50 disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <span className="text-sm text-slate-500">
                                Page {pagination.page} of {pagination.totalPages}
                            </span>
                            <button
                                disabled={pagination.page >= pagination.totalPages}
                                onClick={() => loadTrail(appId, pagination.page + 1)}
                                className="px-3 py-1 text-sm rounded border hover:bg-slate-50 disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AuditTrailPage;
